// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get, post, patch } from './api';
import { ENDPOINTS } from '../utils/constants';
import type {
  OmsOrganization,
  OmsLevel,
  OmsRegion,
  OmsSite,
  OmsSiteCreate,
  OmsSitePatch,
} from '../types';

// -- Organizations ----------------------------------------------------------

export const listOrganizations = async (organizationKey?: string): Promise<OmsOrganization[]> => {
  const url = organizationKey
    ? `${ENDPOINTS.OMS_ORGANIZATIONS}?organizationKey=${encodeURIComponent(organizationKey)}`
    : ENDPOINTS.OMS_ORGANIZATIONS;
  return get<OmsOrganization[]>(url);
};

export const getOrganization = async (orgId: string): Promise<OmsOrganization> => {
  return get<OmsOrganization>(ENDPOINTS.OMS_ORGANIZATION_BY_ID(orgId));
};

// -- Levels -----------------------------------------------------------------

export const listLevels = async (orgId: string, levelKey?: string): Promise<OmsLevel[]> => {
  const base = ENDPOINTS.OMS_LEVELS(orgId);
  const url = levelKey ? `${base}?levelKey=${encodeURIComponent(levelKey)}` : base;
  return get<OmsLevel[]>(url);
};

export const getLevel = async (orgId: string, levelId: string): Promise<OmsLevel> => {
  return get<OmsLevel>(ENDPOINTS.OMS_LEVEL_BY_ID(orgId, levelId));
};

// -- Regions ----------------------------------------------------------------

export const listRegions = async (orgId: string, regionKey?: string): Promise<OmsRegion[]> => {
  const base = ENDPOINTS.OMS_REGIONS(orgId);
  const url = regionKey ? `${base}?regionKey=${encodeURIComponent(regionKey)}` : base;
  return get<OmsRegion[]>(url);
};

export const getRegion = async (orgId: string, regionId: string): Promise<OmsRegion> => {
  return get<OmsRegion>(ENDPOINTS.OMS_REGION_BY_ID(orgId, regionId));
};

// -- Sites ------------------------------------------------------------------

export const listSites = async (orgId: string, siteKey?: string): Promise<OmsSite[]> => {
  const base = ENDPOINTS.OMS_SITES(orgId);
  const url = siteKey ? `${base}?siteKey=${encodeURIComponent(siteKey)}` : base;
  return get<OmsSite[]>(url);
};

export const getSite = async (orgId: string, siteId: string): Promise<OmsSite> => {
  return get<OmsSite>(ENDPOINTS.OMS_SITE_BY_ID(orgId, siteId));
};

export const createSite = async (orgId: string, data: OmsSiteCreate): Promise<OmsSite> => {
  return post<OmsSite>(ENDPOINTS.OMS_SITES(orgId), data);
};

export const updateSite = async (
  orgId: string,
  siteId: string,
  data: OmsSitePatch
): Promise<OmsSite> => {
  return patch<OmsSite>(ENDPOINTS.OMS_SITE_BY_ID(orgId, siteId), data);
};
